import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  BehaviorSubject,
  EMPTY,
  catchError,
  concatMap,
  delay,
  finalize,
  from,
  last,
  map,
  shareReplay,
  switchMap,
  take,
  takeWhile,
  tap,
  timer,
  toArray,
} from 'rxjs';

import { FileUploadsService } from '@app/services/file-uploads.service';
import { PuSubscribable } from '@app/utils/pu-subscribe';
import { InspectionModel } from '@main-application/inspections/models/inspection.model';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

import { InspectionSourceService } from './inspection-source.service';
import { InspectionService } from './inspection.service';
import { InspectionDownloadReportComponent } from '../components/inspections-list/dialogs/inspection-download-report/inspection-download-report.component';
import { InspectionStatus, RestInspectionsModel } from '../models/rest-inspections-model.interface';

export interface DownloadResult {
  success: boolean;
  fileId?: number;
  inspection?: RestInspectionsModel;
  error?: string;
}

@Injectable()
export class InspectionsBulkDownloadService extends PuSubscribable {
  private _progress$ = new BehaviorSubject<number>(0);
  private _isGenerating$ = new BehaviorSubject(false);
  private results: DownloadResult[] = [];
  private modal: MatDialogRef<InspectionDownloadReportComponent, any>;

  public isRunning = false;
  public progress$ = this._progress$.pipe(shareReplay(1));
  public isGenerating$ = this._isGenerating$.pipe(shareReplay(1));
  public completedCount = 0;
  public totalCount = 0;

  constructor(
    private inspectionService: InspectionService,
    private inspectionSourceService: InspectionSourceService,
    private fileService: FileUploadsService,
    private snakbarService: SnackbarService,
    private dialog: MatDialog
  ) {
    super();
  }

  openBulkDownloadDialog() {
    this.modal = this.dialog.open(InspectionDownloadReportComponent, {
      panelClass: 'modal-container-without-padding',
      width: '500px',
      disableClose: true,
    });
  }

  getFailedDownloads(): DownloadResult[] {
    return this.results.filter(r => !r.success);
  }

  runDownloadingProcess(inspectionsToDownload: InspectionModel[] = null) {
    if (this.isRunning) {
      return;
    }
    this.cleanAll();
    this.isRunning = true;
    const inspectionSource = inspectionsToDownload
      ? inspectionsToDownload
      : this.inspectionSourceService.inspectionSource?.filteredData;
    const inspections =
      inspectionSource
        ?.map(inspection => inspection.entityDetails)
        .filter(inspection => inspection.status === InspectionStatus.Completed) || [];

    if (!inspections.length) {
      this.snakbarService.warning('No inspections to download');
      this.isRunning = false;
      return;
    }

    this.openBulkDownloadDialog();
    setTimeout(() => this.runGeneratingProcess(inspections), 300);
  }

  private runGeneratingProcess(inspections: RestInspectionsModel[]) {
    const noAttachments = inspections?.filter(
      inspection => !inspection.attachments.attachment && !inspection.attachments.autoScopeReport
    );
    if (noAttachments?.length) {
      this.generateFiles(noAttachments, inspections);
      return;
    }
    this.downloadAll(inspections);
  }

  private generateFiles(emptyInspections: RestInspectionsModel[], allInspections: RestInspectionsModel[]) {
    if (this._isGenerating$.getValue()) {
      return;
    }
    this._isGenerating$.next(true);
    const finalInspections = [...allInspections];

    const checkFiles$ = (successfulInspections: RestInspectionsModel[]) => {
      this._progress$.next(0);
      this.totalCount = successfulInspections.length;
      this.completedCount = 0;
      const POLL_INTERVAL = 3000;
      const MAX_ATTEMPTS = 10;

      return from(successfulInspections).pipe(
        concatMap(inspection => {
          return timer(0, POLL_INTERVAL).pipe(
            take(MAX_ATTEMPTS),
            concatMap(() => this.inspectionService.getById(inspection.id)),
            tap(inspection => {
              if (this.checkFilesExist(inspection)) {
                const index = finalInspections.findIndex(i => i.id === inspection.id);
                if (index !== -1) {
                  finalInspections.splice(index, 1);
                  finalInspections.push(inspection);
                }
              }
            }),
            takeWhile(inspection => !this.checkFilesExist(inspection)),
            last(undefined, inspection),
            tap(() => {
              this.completedCount++;
              this._progress$.next(this.completedCount / this.totalCount);
            })
          );
        })
      );
    };

    this.regenerateFiles$(emptyInspections)
      .pipe(switchMap(inspections => checkFiles$(inspections)))
      .subscribe({
        complete: () => {
          this._progress$.next(0);
          this._isGenerating$.next(false);
          this.downloadAll(finalInspections);
        },
        error: err => {
          console.error('Generation failed:', err);
          this._isGenerating$.next(false);
        },
      })
      .untilDestroyed(this);
  }

  private regenerateFiles$(emptyInspections: RestInspectionsModel[]) {
    return from(emptyInspections).pipe(
      concatMap(inspection => {
        return this.inspectionService.regenerate(inspection.id).pipe(
          delay(1000),
          catchError(error => {
            console.error(`Failed to generate file for inspection ${inspection.id}:`, error);
            let errorMessage = error?.error?.message || error?.message;
            errorMessage = `Failed to generate file for inspection ${inspection.id} / ` + errorMessage;
            this.results.push(this.createErrorResult(inspection, errorMessage));
            return EMPTY;
          }),
          map(() => inspection)
        );
      }),
      toArray()
    );
  }

  private downloadAll(inspections: RestInspectionsModel[]) {
    const allFiles = inspections?.flatMap(inspection => {
      return [
        inspection.attachments?.autoScopeReport?.fileUploadId,
        inspection.attachments?.attachment?.fileUploadId,
      ].filter(Boolean);
    });
    this.totalCount = allFiles?.length ?? 0;
    this.completedCount = 0;
    from(allFiles)
      .pipe(
        delay(500),
        concatMap((fileId, index) => {
          this.completedCount++;
          this._progress$.next(this.completedCount / this.totalCount);
          return this.fileService.downloadFile$(fileId).pipe(
            delay(index < allFiles?.length - 1 ? 1000 : 0),
            catchError(error => {
              console.error(`Failed to download ${fileId}:`, error);
              const inspection = inspections.find(
                i =>
                  i.attachments?.autoScopeReport?.fileUploadId === fileId ||
                  i.attachments?.attachment?.fileUploadId === fileId
              );
              let errorMessage = error?.error?.message || error?.message;
              errorMessage = `Failed to download ${fileId} / ` + errorMessage;
              this.results.push(this.createErrorResult(inspection, errorMessage, fileId));
              return EMPTY;
            })
          );
        }),
        finalize(() => {
          this.isRunning = false;
        })
      )
      .subscribe({
        complete: () => {
          if (this.results.every(r => r.success)) {
            this.modal.close();
            this.cleanAll();
          }
        },
        error: err => {
          console.error('Download failed:', err);
        },
      })
      .untilDestroyed(this);
  }

  private createErrorResult(inspection: RestInspectionsModel, error: string, fileId?: number): DownloadResult {
    return { success: false, inspection, fileId, error: error };
  }

  private checkFilesExist(inspection: RestInspectionsModel): boolean {
    return (
      !!inspection.attachments?.autoScopeReport?.fileUploadId || !!inspection.attachments?.attachment?.fileUploadId
    );
  }

  cleanAll(): void {
    this.isRunning = false;
    this.totalCount = 0;
    this.completedCount = 0;
    this._progress$.next(0);
    this._isGenerating$.next(false);
    this.results = [];
  }
}
